import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface PriceTable {
  id: number;
  descricao?: string;
  ordemBusca: number;
  isAtiva: boolean;
}

export interface PriceTableInsert {
  descricao?: string;
  ordemBusca?: number;
  isAtiva?: boolean;
}

export const usePriceTables = () => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["price-tables"],
    queryFn: async () => {
      const { data } = await apiClient.get<PriceTable[]>("/api/tabelas-preco");
      return data;
    },
  });

  const activeQuery = useQuery({
    queryKey: ["price-tables", "active"],
    queryFn: async () => {
      const { data } = await apiClient.get<PriceTable[]>("/api/tabelas-preco", { params: { ativo: true } });
      return data;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (body: PriceTableInsert) => {
      const { data } = await apiClient.post<PriceTable>("/api/tabelas-preco", body);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["price-tables"] });
      toast.success("Tabela de preço criada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao criar tabela de preço: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: PriceTableInsert }) => {
      const { data: updated } = await apiClient.put<PriceTable>(`/api/tabelas-preco/${id}`, data);
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["price-tables"] });
      toast.success("Tabela de preço atualizada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao atualizar tabela de preço: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/tabelas-preco/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["price-tables"] });
      toast.success("Tabela de preço excluída com sucesso!");
    },
    onError: (error: Error) => {
      toast.error(`Erro ao excluir tabela de preço: ${error.message}`);
    },
  });

  return {
    priceTables:      query.data,
    activePriceTables: activeQuery.data,
    isLoading:        query.isLoading || activeQuery.isLoading,
    error:            query.error || activeQuery.error,
    createPriceTable: createMutation.mutate,
    updatePriceTable: updateMutation.mutate,
    deletePriceTable: deleteMutation.mutate,
    isCreating:       createMutation.isPending,
    isUpdating:       updateMutation.isPending,
    isDeleting:       deleteMutation.isPending,
  };
};
